import { LoadingSpinner } from '@opengov/component-library/capital';
import { IMergeTags, IStory, IStoryContent, IStoryCustomColors } from '@opengov/stories-shared/types';
import { initializeCustomColors } from '@opengov/stories-shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import { MergeTagsProvider } from '../../common/context/MergeTagsContext';
import { IEditorMessages, IModalMessages, IUpdateCustomColorProps } from '../../../types';

import { IStoryContentsFrameProps, StoryContentsFrame } from '../../utils/StoryContentsFrame/story';
import { EMPTY_SETTINGS } from '../StoriesSettings/emptySettings';
import { useFetchSettings } from '../StoriesSettings/hooks/useFetchSettings';
import {
  storyViewQueryKeys,
  StoryContentMetaProvider,
  useStoryContentMeta,
  useStory,
  useStoryContents,
  useProposalsInStory,
  useUpdateStoryCustomColors,
  useSaveProgressTrackerConfig,
} from '../StoryView/hooks';

import styles from './StoryPreview.scss';
import { PageErrorBoundary } from '../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../instrumentation/views';
import { ProgressTrackerSettingsProvider } from '../StoriesSettings/ProgressTrackerSettings/context/ProgressTracker.Provider';

export interface IStoryPreviewProps {
  storyId: string;
  entityId: number;
  entitySubdomain: string;
  entityUuid: string;
  entityName: string;
  isTemplate: boolean;
  canAddProposalContent: boolean;
  canEditPageBreaks: boolean;
  canEditSpacing: boolean;
  canUseAiToGenerateContent: boolean;
  canUseStoriesSummarizerGA: boolean;
  canUseOgAssistGenerateSummary: boolean;
  isAiToggleFeatureEnabled: boolean;
  isCustomSpreadsheetReportEnabled: boolean;
  showProgressTracker: boolean;
  openModal(modal: string): IAction<string, string>;
  messages: IEditorMessages;
  modalMessages: IModalMessages;
}

const StoryPreviewInternal = (props: IStoryPreviewProps) => {
  const {
    storyId,
    entityId,
    entitySubdomain,
    entityUuid,
    entityName,
    isTemplate,
    canAddProposalContent,
    canEditPageBreaks,
    canEditSpacing,
    canUseAiToGenerateContent,
    canUseStoriesSummarizerGA,
    canUseOgAssistGenerateSummary,
    isAiToggleFeatureEnabled,
    isCustomSpreadsheetReportEnabled,
    showProgressTracker,
    openModal,
    messages,
    modalMessages,
  } = props;

  const queryClient = useQueryClient();
  const metaCtx = useStoryContentMeta();
  const { mutateAsync: doUpdateStoryCustomColors } = useUpdateStoryCustomColors();
  const { mutateAsync: doSaveProgressTrackerConfig } = useSaveProgressTrackerConfig();

  const storyQueryParams = useMemo(() => ({ storyId, isTemplate }), [storyId, isTemplate]);
  const storyContentsQueryParams = useMemo(
    () => (entityId > 0 ? { storyId, entityId, isTemplate } : null),
    [storyId, entityId, isTemplate]
  );
  const proposalsQueryParams = useMemo(
    () => (canAddProposalContent && entityId > 0 ? { storyId, entityId } : null),
    [canAddProposalContent, storyId, entityId]
  );

  const storyQuery = useStory(storyQueryParams, (messages as any).somethingWentWrongError ?? '');
  const storyContentsQuery = useStoryContents(storyContentsQueryParams, (messages as any).somethingWentWrongError ?? '');
  const proposalsQuery = useProposalsInStory(proposalsQueryParams, (messages as any).somethingWentWrongError ?? '');

  const story = storyQuery.data as IStory;
  const storyContents = (storyContentsQuery.data ?? []) as IStoryContent[];
  const mergeTags = proposalsQuery.data?.mergeTags ?? ({} as IMergeTags);
  // Fetch settings directly (react-query caches per entityId); EMPTY_SETTINGS
  // covers the brief cold-load window before the fetch resolves.
  const { data: fetchedSettings } = useFetchSettings({ entityId });
  const settings = fetchedSettings ?? EMPTY_SETTINGS;
  const isLoading = storyQuery.isLoading;
  const isLoadingProposals = proposalsQuery.isLoading;

  const storyCustomColors = useMemo(
    () => (story ? initializeCustomColors(story) : ({} as IStoryCustomColors)),
    [story]
  );

  const updateStoryContent = useCallback(
    (storyContent: IStoryContent) => {
      if (!storyContentsQueryParams) return;
      queryClient.setQueryData(
        storyViewQueryKeys.storyContents(storyContentsQueryParams),
        (old: IStoryContent[] | undefined) =>
          old?.map((sc) => (sc._id === storyContent._id ? storyContent : sc)) ?? []
      );
    },
    [queryClient, storyContentsQueryParams]
  );

  const updateStoryContents = useCallback(
    (newContents: IStoryContent[]) => {
      if (!storyContentsQueryParams) return;
      queryClient.setQueryData(storyViewQueryKeys.storyContents(storyContentsQueryParams), newContents);
    },
    [queryClient, storyContentsQueryParams]
  );

  const updateStoryCustomColors = useCallback(
    (p: IUpdateCustomColorProps) => doUpdateStoryCustomColors(p),
    [doUpdateStoryCustomColors]
  );

  const saveProgressTrackerConfig = useCallback(
    (p: Omit<Parameters<typeof doSaveProgressTrackerConfig>[0], 'entityId'>): Promise<void> =>
      doSaveProgressTrackerConfig({ ...p, entityId }).then(() => undefined),
    [doSaveProgressTrackerConfig, entityId]
  );

  if (isLoading || !story) {
    return <LoadingSpinner className={styles.loadingPosition} variant="page" />;
  }

  return (
    <main className={styles.root} role="main" id="mainContent">
      {isLoadingProposals || !story.title || storyContents.length < 1 ? (
        <LoadingSpinner className={styles.loadingPosition} variant="page" />
      ) : (
        <div className={styles.wrapper}>
          <MergeTagsProvider
            mergeTags={mergeTags}
            storyContents={storyContents}
            canAddProposalContent={canAddProposalContent}
          >
            <StoryContentsFrame
              story={story}
              storyContents={storyContents}
              storyTitle={story.title}
              storyDescription={story.description}
              storyCustomColors={storyCustomColors}
              settings={settings}
              entityId={entityId}
              entitySubdomain={entitySubdomain}
              entityUuid={entityUuid}
              entityName={entityName}
              isTemplate={isTemplate}
              isReadOnly={true}
              canAddProposalContent={canAddProposalContent}
              canEditPageBreaks={canEditPageBreaks}
              canEditSpacing={canEditSpacing}
              canUseAiToGenerateContent={canUseAiToGenerateContent}
              canUseStoriesSummarizerGA={canUseStoriesSummarizerGA}
              canUseOgAssistGenerateSummary={canUseOgAssistGenerateSummary}
              isAiToggleFeatureEnabled={isAiToggleFeatureEnabled}
              isCustomSpreadsheetReportEnabled={isCustomSpreadsheetReportEnabled}
              showProgressTracker={showProgressTracker}
              openModal={openModal}
              messages={messages}
              modalMessages={modalMessages}
              updateStoryContent={updateStoryContent as IStoryContentsFrameProps['updateStoryContent']}
              updateStoryContents={updateStoryContents as IStoryContentsFrameProps['updateStoryContents']}
              updateMetaStoryContent={metaCtx.setStoryContent as IStoryContentsFrameProps['updateMetaStoryContent']}
              updateMetaStoryContentOrder={metaCtx.setStoryContentOrder as IStoryContentsFrameProps['updateMetaStoryContentOrder']}
              updateMetaStoryContentInView={metaCtx.setStoryContentInView as IStoryContentsFrameProps['updateMetaStoryContentInView']}
              updateStoryCustomColors={updateStoryCustomColors as unknown as IStoryContentsFrameProps['updateStoryCustomColors']}
              saveProgressTrackerConfig={saveProgressTrackerConfig as unknown as IStoryContentsFrameProps['saveProgressTrackerConfig']}
            />
          </MergeTagsProvider>
        </div>
      )}
    </main>
  );
};

export const StoryPreview = (props: IStoryPreviewProps) => {
  return (
    <PageErrorBoundary viewName={ViewName.StoryPreviewPage}>
      <StoryContentMetaProvider>
        <ProgressTrackerSettingsProvider entityId={props.entityId}>
          <StoryPreviewInternal {...props} />
        </ProgressTrackerSettingsProvider>
      </StoryContentMetaProvider>
    </PageErrorBoundary>
  );
};
